import { Button, Campaign, Input, LogService, Overlay, Selector, Tip, ToastHelper, UserInDetails, VisionService } from '@hub/shared'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './overlay-content-link.component.scoped.scss'
import './overlay-content-link.component.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean
  setOpen: (value: boolean) => void
  campaign: Campaign
  userInCampaign: UserInDetails
}

export default ({ open = false, setOpen, campaign, userInCampaign }: Props) => {
  const [t] = useTranslation('overlayContentLink')
  const toast = useMemo(() => new ToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  const visionService = useMemo(() => new VisionService(), [])
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [link, setLink] = useState('')
  const [isCollab, setIsCollab] = useState(false)

  logService.init('Overlay Content Link')

  const isLinkTrue = (value: string) => {
    if (!loadingSubmit) {
      try {
        let url = new URL(value)
        return true
      } catch (err) {
        return false
      }
    }
  }

  const getMediaId = (linkParam: string) => {
    if (linkParam.includes('/p/')) {
      return linkParam.split('/p/')[1].split('/')[0]
    } else if (linkParam.includes('/tv/')) {
      return linkParam.split('/tv/')[1].split('/')[0]
    } else if (linkParam.includes('/reel/')) {
      return linkParam.split('/reel/')[1].split('/')[0]
    } else {
      return ''
    }
  }

  const submit = useCallback(() => {
    if (!loadingSubmit) {
      setLoadingSubmit(true)
      visionService
        .postUploadMedia(
          campaign?._id,
          campaign?.socialNetworks[0] as 'instagram' | 'tiktok',
          getMediaId(link),
          isCollab,
          userInCampaign.username,
          link
        )
        .then((result) => {
          setOpen(false)
          setLoadingSubmit(false)
          toast.success(t('success'), {
            closeButton: true,
            className: 'full text-bold',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            notOverClick: true,
            immediately: true,
          })
        })
        .catch((err) => {
          const EXPECTED_ERRORS = ['INVALID_CAMPAIGN', 'ERROR_COPYRIGHT', 'ERROR_SCRAPPER', 'INVALID_CAMPAIGN_PERIOD']
          const message = EXPECTED_ERRORS.includes(err?.data?.message) ? t(err?.data?.message) : t('error')
          setLoadingSubmit(false)
          toast.error(message, {
            closeButton: true,
            className: 'full',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 3600000,
            notOverClick: true,
            immediately: true,
          })
          logService.error({
            message: 'Error submitting media',
            error: err,
          })
        })
    }
  }, [loadingSubmit, campaign, userInCampaign, link, isCollab, visionService, setOpen, toast, t, logService])

  let instagramWarning = null
  if (campaign?.socialNetworks?.[0] === 'instagram') {
    instagramWarning = (
      <div className="instagram-warning">
        <i className="fa-regular fa-lightbulb-on"></i>
        <div>
          <p className="ml-3">{t('instagramWarning')}</p>
          <p className="ml-3">{t('courselWarning')}</p>
        </div>
      </div>
    )
  }

  const Header = () => (
    <div className="header">
      <i
        className="fa-regular fa-arrow-left-long"
        style={{ color: 'var(--white)', cursor: !loadingSubmit ? 'pointer' : 'not-allowed', fontSize: '18px' }}
        onClick={() => (!loadingSubmit ? setOpen(false) : null)}
        role="button"
        tabIndex={0}
      ></i>
      <h5 className="ml-3">{t('title')}</h5>
    </div>
  )

  const Footer = () => (
    <div className="footer">
      <Button
        onClick={() => setOpen(false)}
        disabled={loadingSubmit}
        color="var(--background_secondary)"
        borderColor="var(--text_color)"
        textColor="var(--text_color)"
        inverted
      >
        {t('cancel')}
      </Button>
      <Button onClick={submit} loading={loadingSubmit} disabled={!isLinkTrue(link) || loadingSubmit}>
        {t('save')}
      </Button>
    </div>
  )

  if (!campaign?._id || !['instagram', 'tiktok'].includes(campaign?.socialNetworks?.[0])) {
    return null
  }

  return (
    <Overlay
      open={open}
      onCloseChange={() => {
        setOpen(false)
        setLink('')
        setIsCollab(false)
      }}
      buttonClose={false}
      header={<Header />}
      headerColor="var(--black_medium)"
      footer={<Footer />}
    >
      <section>
        <Input
          className="mb-3"
          name="input-content-link"
          id="input-content-link"
          label={t('link')}
          placeholder="https://"
          value={link}
          onChange={(value) => setLink(value)}
        />
      </section>

      {campaign?.socialNetworks[0] === 'instagram' ? (
        <Selector
          className="campaign-is-collab"
          errorSpan={false}
          checked={isCollab}
          onChange={(collab: boolean) => setIsCollab(collab)}
          toggle
          name="campaign-is-collab"
          id="campaign-is-collab"
          leftLabel={
            <span>
              {t('campaignIsCollab')}
              <Tip className="ml-2" message={t('helpTextIsCollab')}></Tip>
            </span>
          }
        />
      ) : null}
      {instagramWarning}
    </Overlay>
  )
}
